import React, { useEffect, useState } from 'react';
import './FeatureStyles.css';
import './AITestHealingFeature.css';

const AITestHealingAnimation = () => {
  const [phase, setPhase] = useState(0);
  // 0: running (connected), 1: failed (broken), 2: analyzing, 3: suggestion (new path), 4: fixed

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(prev => (prev + 1) % 5);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="feature-animation abstract ai-healing-abstract">
      <svg className="abstract-canvas" viewBox="0 0 320 200">
        <defs>
          <linearGradient id="healing-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>

        {/* Left node (selector/source) */}
        <g className="left-node-group">
          <circle
            className={`abstract-node ${phase === 0 || phase === 4 ? 'blue active' : phase === 1 ? 'gray' : 'blue'}`}
            cx="70"
            cy="100"
            r="24"
          />
        </g>

        {/* Right node (element/target) */}
        <g className="right-node-group">
          <circle
            className={`abstract-node ${phase === 4 ? 'blue active' : phase === 1 ? 'gray' : 'blue'}`}
            cx="250"
            cy="100"
            r="24"
          />
        </g>

        {/* Original connection line */}
        {phase < 4 && (
          <path
            className={`abstract-line ${phase === 0 ? 'blue' : 'broken'}`}
            d="M 94 100 L 226 100"
          />
        )}

        {/* Traveling particle (phase 0) */}
        {phase === 0 && (
          <circle className="traveling-particle healing-particle" r="5">
            <animateMotion dur="1.5s" repeatCount="indefinite" path="M 94 100 L 226 100" />
          </circle>
        )}

        {/* Break indicator (phase 1) */}
        {phase === 1 && (
          <g className="break-indicator">
            <line className="break-line" x1="155" y1="90" x2="165" y2="110" />
            <line className="break-line" x1="165" y1="90" x2="155" y2="110" />
          </g>
        )}

        {/* AI scanning pulse (phase 2) */}
        {phase === 2 && (
          <g className="ai-scan">
            <circle className="scan-pulse" cx="160" cy="100" r="8" />
            <circle className="scan-pulse" cx="160" cy="100" r="8" style={{ animationDelay: '0.5s' }} />
          </g>
        )}

        {/* New healed path (phases 3-4) */}
        {phase >= 3 && (
          <path
            className={`abstract-line ${phase === 3 ? 'pink dashed' : 'gradient'} new-path`}
            d="M 94 100 Q 160 60 226 100"
            style={{ stroke: phase === 4 ? 'url(#healing-gradient)' : undefined }}
          />
        )}

        {/* Healing particle traveling on new path (phase 3) */}
        {phase === 3 && (
          <circle className="traveling-particle pink" r="5">
            <animateMotion dur="1s" repeatCount="indefinite" path="M 94 100 Q 160 60 226 100" />
          </circle>
        )}

        {/* Success checkmark (phase 4) */}
        {phase === 4 && (
          <g className="success-check" transform="translate(160, 55)">
            <circle className="abstract-node blue" cx="0" cy="0" r="12" />
            <path
              className="check-mark"
              d="M -5 0 L -2 3 L 5 -4"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>

      {/* Result label */}
      {phase === 4 && <span className="result-label">Fixed</span>}
    </div>
  );
};

const AITestHealingFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4.93 4.93l4.24 4.24m5.66 5.66l4.24 4.24M12 12l-4.24 4.24m0-8.48l4.24 4.24m4.24-4.24l-4.24 4.24m4.24 4.24l-4.24-4.24" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
  title: "AI Test Healing",
  description: "Tests break. AI fixes them. When selectors change or elements move, our AI analyzes the DOM differences and suggests precise healing fixes. Distinguish between real bugs and UI changes. Apply fixes with a single click - 80% less maintenance guaranteed.",
  animation: <AITestHealingAnimation />
};

export default AITestHealingFeature;
